/**
 * Per-device persistence: the player's display name and the session token for each room they joined.
 * Everything is wrapped in try/catch because storage can be unavailable (private mode, blocked, SSR).
 */
export interface StoredSession {
  code: string
  token: string
  playerId: string
  name: string
  at: number
}

const NAME_KEY = 'onuw.name'
const SESSION_KEY = (code: string) => `onuw.session.${code}`
const SESSION_TTL_MS = 12 * 60 * 60 * 1000

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

export const loadName = (): string => read(NAME_KEY) ?? ''
export const saveName = (name: string) => write(NAME_KEY, name.trim())

export const loadSession = (code: string): StoredSession | null => {
  const raw = read(SESSION_KEY(code))
  if (!raw) return null
  try {
    const s = JSON.parse(raw) as StoredSession
    if (!s.token || Date.now() - s.at > SESSION_TTL_MS) return null
    return s
  } catch {
    return null
  }
}
export const saveSession = (s: StoredSession) => write(SESSION_KEY(s.code), JSON.stringify(s))
export const clearSession = (code: string) => write(SESSION_KEY(code), null)

/** Small per-viewer preferences (sound, haptics, reduced effects). */
export type Prefs = { sound: boolean; haptics: boolean }
const PREFS_KEY = 'onuw.prefs'
export const loadPrefs = (): Prefs => {
  try {
    return { sound: true, haptics: true, ...(JSON.parse(read(PREFS_KEY) ?? '{}') as Partial<Prefs>) }
  } catch {
    return { sound: true, haptics: true }
  }
}
export const savePrefs = (p: Prefs) => write(PREFS_KEY, JSON.stringify(p))

/** How wide the reader made the chat on a wide screen (px), or null for the column's own width. */
const CHAT_WIDTH_KEY = 'onuw.chatWidth'
export const loadChatWidth = (): number | null => {
  const width = Number(read(CHAT_WIDTH_KEY))
  return Number.isFinite(width) && width > 0 ? width : null
}
export const saveChatWidth = (width: number | null) =>
  write(CHAT_WIDTH_KEY, width === null ? null : String(Math.round(width)))

/** How the role lists (roles page, lobby picker) are ordered. */
export type RoleOrder = 'expansion' | 'wake'
const ROLE_ORDER_KEY = 'onuw.roleOrder'
export const loadRoleOrder = (): RoleOrder => (read(ROLE_ORDER_KEY) === 'wake' ? 'wake' : 'expansion')
export const saveRoleOrder = (order: RoleOrder) => write(ROLE_ORDER_KEY, order)
